import React, { useEffect, useState } from 'react';
import ApiService from '../../../../../services/ApiService';
import { stripHtml } from '../../../../../utils/stripHtml';
import {
  Overlay,
  ModalContainer,
  Title,
  Subtitle,
  EmptyState,
  Table,
  Th,
  Td,
  ItemRow,
  MotivoInelegibilidade,
  DescricaoRiscada,
  BadgesRow,
  BadgePendencia,
  ValorFixo,
  Legenda,
  LegendaItem,
  LegendaDot,
  QtyInput,
  ValueInput,
  Actions,
  OutlineButton,
  PrimaryButton,
} from './style';

interface Props {
  invoiceId: number;
  orderId: number;
  onClose: () => void;
  onAdded: () => void;
  onError: (msg: string) => void;
}

// Modal de adição de itens a uma nota existente (rascunho). Reusa os itens elegíveis do
// pedido (saldo já descontado pelo backend) e chama addInvoiceItems — não cria nota nova.

// O DTO do fiscal valida no máximo 4 casas decimais em valorUnitario/valorTotal.
const round4 = (value: number) => Math.round(value * 10000) / 10000;

const AddInvoiceItemsModal: React.FC<Props> = ({ invoiceId, orderId, onClose, onAdded, onError }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await ApiService.fiscal.getEligibleNfeItems(orderId);
        setItems(
          (Array.isArray(data) ? data : []).map((i: any) => ({
            ...i,
            selected: false,
            qtd: '',
            valor: i.valorUnitarioSugerido != null ? String(i.valorUnitarioSugerido) : '',
          })),
        );
      } catch {
        onError('Erro ao carregar itens disponíveis do pedido.');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId, onClose, onError]);

  const toggle = (idx: number) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, selected: !it.selected } : it)));

  const setQtd = (idx: number, value: string) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, qtd: value } : it)));

  const setValor = (idx: number, value: string) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, valor: value } : it)));

  const handleConfirm = async () => {
    const selecionados = items.filter((it) => it.selected);
    if (selecionados.length === 0) {
      onError('Selecione ao menos um item e informe uma quantidade válida.');
      return;
    }
    if (
      selecionados.some(
        (it) => !(Number(it.qtd) > 0) || !Number.isInteger(Number(it.qtd)) || !(Number(it.valor) > 0),
      )
    ) {
      onError('Informe quantidade inteira e valor unitário válidos para os itens selecionados.');
      return;
    }
    const payload = selecionados.map((it) => {
      const quantidade = Math.floor(Number(it.qtd));
      const valorUnitario = round4(Number(it.valor));
      return {
        orderItemId: it.id,
        quantidade,
        valorUnitario,
        valorTotal: round4(quantidade * valorUnitario),
        ...(it.unitOfMeasurement?.id != null ? { unitOfMeasurementId: it.unitOfMeasurement.id } : {}),
      };
    });
    setSaving(true);
    try {
      await ApiService.fiscal.addInvoiceItems(invoiceId, payload);
      onAdded();
    } catch (error: any) {
      onError(error?.response?.data?.message || 'Erro ao adicionar itens à nota.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Title>Adicionar itens do pedido</Title>
        <Subtitle>Somente itens com saldo disponível no pedido {orderId}.</Subtitle>

        {loading ? (
          <EmptyState>Carregando…</EmptyState>
        ) : items.length === 0 ? (
          <EmptyState>Nenhum item disponível para adicionar.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Adicionar</Th>
                <Th>Item</Th>
                <Th>Unidade</Th>
                <Th>Valor unit.</Th>
                <Th>Disponível</Th>
                <Th>Qtd</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <ItemRow key={it.id} $inelegivel={it.elegivel === false || it.totalmenteFaturado === true}>
                  <Td>
                    <input
                      type="checkbox"
                      checked={it.selected}
                      disabled={it.elegivel === false || it.totalmenteFaturado === true}
                      onChange={() => toggle(idx)}
                    />
                  </Td>
                  <Td>
                    {it.totalmenteFaturado === true ? (
                      <DescricaoRiscada>{stripHtml(it.descricao) || `Item ${it.id}`}</DescricaoRiscada>
                    ) : (
                      stripHtml(it.descricao) || `Item ${it.id}`
                    )}
                    {(it.pendencias ?? []).length > 0 && (
                      <BadgesRow>
                        {it.pendencias.map((p: any) => (
                          <BadgePendencia key={p.label} $bloqueante={p.bloqueante} title={p.descricao}>
                            {p.label}
                          </BadgePendencia>
                        ))}
                      </BadgesRow>
                    )}
                    {it.totalmenteFaturado === true && (
                      <MotivoInelegibilidade>Item já faturado — sem saldo disponível</MotivoInelegibilidade>
                    )}
                  </Td>
                  <Td>{it.unitOfMeasurement?.name || '—'}</Td>
                  <Td>
                    {it.valorUnitarioSugerido != null ? (
                      <ValorFixo title="Valor da venda — ajustes só na edição da nota">
                        {it.valor}
                      </ValorFixo>
                    ) : (
                      <ValueInput
                        type="number"
                        min={0}
                        step={0.0001}
                        value={it.valor}
                        disabled={!it.selected}
                        onChange={(e) => setValor(idx, e.target.value)}
                      />
                    )}
                  </Td>
                  <Td>{it.quantidadeDisponivel}</Td>
                  <Td>
                    <QtyInput
                      type="number"
                      min={1}
                      step={1}
                      max={it.quantidadeDisponivel}
                      value={it.selected ? it.qtd : ''}
                      disabled={!it.selected}
                      onChange={(e) => setQtd(idx, e.target.value)}
                    />
                  </Td>
                </ItemRow>
              ))}
            </tbody>
          </Table>
        )}

        {items.some((i) => (i.pendencias ?? []).length > 0) && (
          <Legenda>
            <LegendaItem><LegendaDot $bloqueante /> impede faturar</LegendaItem>
            <LegendaItem><LegendaDot /> pendência para a emissão</LegendaItem>
          </Legenda>
        )}

        <Actions>
          <OutlineButton onClick={onClose} disabled={saving}>Cancelar</OutlineButton>
          <PrimaryButton onClick={handleConfirm} disabled={saving || loading}>
            {saving ? 'Adicionando…' : 'Adicionar'}
          </PrimaryButton>
        </Actions>
      </ModalContainer>
    </Overlay>
  );
};

export default AddInvoiceItemsModal;
